import React, { useEffect } from 'react';
import { 
  FiX, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiInfo, 
  FiAlertTriangle,
  FiLock,
  FiShield,
  FiDownload,
  FiUpload,
  FiSave,
  FiCopy,
  FiShare
} from 'react-icons/fi';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true,
  type = 'default', // default, success, error, warning, info
  footer,
  showBackdrop = true,
  showHeader = true,
  showDivider = true,
  className = '',
  animation = 'fade-in' // fade-in, slide-up, scale
}) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    xs: 'max-w-sm',
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    full: 'max-w-full mx-4 md:mx-8'
  };

  const typeConfig = {
    default: { color: 'text-[#ff660e]', bg: 'bg-[#ff660e]/10', icon: null },
    success: { color: 'text-green-400', bg: 'bg-green-900/20', icon: <FiCheckCircle /> },
    error: { color: 'text-red-400', bg: 'bg-red-900/20', icon: <FiAlertCircle /> },
    warning: { color: 'text-yellow-400', bg: 'bg-yellow-900/20', icon: <FiAlertTriangle /> },
    info: { color: 'text-blue-400', bg: 'bg-blue-900/20', icon: <FiInfo /> },
    secure: { color: 'text-purple-400', bg: 'bg-purple-900/20', icon: <FiShield /> }
  };

  const animationClasses = {
    'fade-in': 'animate-fade-in',
    'slide-up': 'animate-slide-up',
    'scale': 'animate-scale-in'
  };

  const config = typeConfig[type] || typeConfig.default;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      {showBackdrop && (
        <div
          className={`fixed inset-0 bg-black/70 transition-opacity ${
            animation === 'fade-in' ? 'animate-fade-in' : ''
          }`}
          onClick={closeOnOverlayClick ? onClose : undefined}
        />
      )}

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full ${sizeClasses[size]} ${
          animationClasses[animation] || animationClasses['fade-in']
        } ${className}`}>
          {/* Header */}
          {showHeader && (
            <div className={`px-6 py-4 border-b border-gray-700 ${showDivider ? '' : 'pb-2'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {config.icon && (
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bg}`}>
                      <span className={`text-xl ${config.color}`}>
                        {config.icon}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-white">{title}</h3>
                    {type !== 'default' && (
                      <p className="text-sm text-gray-400 mt-1">
                        {type === 'success' && 'Operation completed successfully'}
                        {type === 'error' && 'An error occurred'}
                        {type === 'warning' && 'Please review this warning'}
                        {type === 'info' && 'Important information'}
                        {type === 'secure' && 'Secure operation required'}
                      </p>
                    )}
                  </div>
                </div>
                
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors group"
                    aria-label="Close modal"
                  >
                    <FiX className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div className={`p-6 max-h-[70vh] overflow-y-auto custom-scrollbar ${
            !showHeader && !footer ? 'rounded-xl' : ''
          }`}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className={`px-6 py-4 border-t border-gray-700 bg-gray-800/50 rounded-b-xl ${
              showDivider ? '' : 'pt-2'
            }`}>
              {footer}
            </div>
          )}
        </div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6B7280;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
      `}</style>
    </div>
  );
};

// Helper component for modal actions
Modal.Actions = ({ children, className = '' }) => (
  <div className={`flex justify-end space-x-3 ${className}`}>
    {children}
  </div>
);

// Helper component for modal body
Modal.Body = ({ children, className = '' }) => (
  <div className={`space-y-4 ${className}`}>
    {children}
  </div>
);

// Helper component for modal title
Modal.Title = ({ children, icon, className = '' }) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    {icon && <span className="text-xl">{icon}</span>}
    <h3 className="text-lg font-semibold text-white">{children}</h3>
  </div>
);

// Helper component for modal description
Modal.Description = ({ children, className = '' }) => (
  <p className={`text-gray-400 ${className}`}>
    {children}
  </p>
);

// Helper component for modal icon
Modal.Icon = ({ icon, type = 'default', className = '' }) => {
  const config = {
    default: 'text-gray-400 bg-gray-700',
    success: 'text-green-400 bg-green-900/20',
    error: 'text-red-400 bg-red-900/20',
    warning: 'text-yellow-400 bg-yellow-900/20',
    info: 'text-blue-400 bg-blue-900/20',
    primary: 'text-[#ff660e] bg-[#ff660e]/10'
  };
  
  return (
    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${config[type]} ${className}`}>
      {icon}
    </div>
  );
};

// Pre-styled modal types
Modal.Confirm = ({ 
  title = "Confirm Action",
  message,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
  ...props
}) => (
  <Modal
    title={title}
    type={type}
    footer={
      <Modal.Actions>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium"
        >
          {cancelText}
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 text-white rounded-lg transition-colors font-medium ${
            type === 'error' ? 'bg-red-600 hover:bg-red-700' :
            type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
            type === 'success' ? 'bg-green-600 hover:bg-green-700' :
            'bg-[#ff660e] hover:bg-orange-600'
          }`}
        >
          {confirmText}
        </button>
      </Modal.Actions>
    }
    {...props}
  >
    <Modal.Body>
      <p className="text-gray-300">{message}</p>
    </Modal.Body>
  </Modal>
);

Modal.Success = ({ title = "Success", message, onClose, ...props }) => (
  <Modal
    title={title}
    type="success"
    footer={
      <Modal.Actions>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium"
        >
          Continue
        </button>
      </Modal.Actions>
    }
    {...props}
  >
    <Modal.Body>
      <div className="text-center">
        <Modal.Icon icon={<FiCheckCircle className="text-4xl" />} type="success" className="mx-auto mb-4" />
        <p className="text-gray-300">{message}</p>
      </div>
    </Modal.Body>
  </Modal>
);

Modal.Error = ({ title = "Error", message, onClose, ...props }) => (
  <Modal
    title={title}
    type="error"
    footer={
      <Modal.Actions>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
        >
          Close
        </button>
      </Modal.Actions>
    }
    {...props}
  >
    <Modal.Body>
      <div className="text-center">
        <Modal.Icon icon={<FiAlertCircle className="text-4xl" />} type="error" className="mx-auto mb-4" />
        <p className="text-gray-300">{message}</p>
      </div>
    </Modal.Body>
  </Modal>
);

export default Modal;